// =============================================================================
// SiteListDialog.jsx
//
// Popup para seleccionar quais sites incluir num server.
//
// PORQUÊ está num ficheiro separado e é renderizado no TrackerPage?
//   O MUI renderiza cada Dialog num React Portal (nó directo no <body>).
//   Quando este Dialog fecha, a animação de saída dispara eventos de clique
//   que o MUI interpreta como "backdrop click do Dialog pai aberto".
//   Resultado: o TrackerModal fechava automaticamente ao confirmar os sites.
//
//   Solução: este Dialog vive no TrackerPage, ao mesmo nível que o TrackerModal.
//   Os dois Dialogs são irmãos independentes — o fecho de um não afecta o outro.
//
// Fluxo:
//   1. Utilizador clica "Site List" num ServerCard dentro do TrackerModal
//   2. TrackerModal chama props.onOpenSiteList(server, updateServerFn)
//   3. TrackerPage guarda o server, a callback, e abre este Dialog
//   4. Utilizador selecciona sites e clica "Confirm"
//   5. TrackerPage chama updateServerFn(selectedSites) → actualiza o form
//   6. TrackerPage fecha este Dialog
//
// Props:
//   open      → boolean
//   server    → { omcId, tac, tacs?, siteToTac?, allSites, selectedSites } | null
//   onClose   → fechar sem guardar
//   onConfirm → callback(selectedSites: string[])
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, IconButton, Box,
  Divider, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Checkbox, InputAdornment, Paper, Chip, Collapse,
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import SearchIcon       from '@mui/icons-material/Search';
import PlaylistAddIcon  from '@mui/icons-material/PlaylistAdd';

// Cores fixas para os 3 valores conhecidos de Side — puramente visual,
// qualquer valor desconhecido cai no fallback cinzento.
const SIDE_COLORS = {
  TFinTF: { bg: '#E3F2FD', fg: '#1565C0' },
  TFinVF: { bg: '#F3E5F5', fg: '#6A1B9A' },
  VFinTF: { bg: '#E8F5E9', fg: '#2E7D32' },
};
const sideColor = (side) => SIDE_COLORS[side] || { bg: 'grey.200', fg: 'text.secondary' };

const SiteListDialog = ({ open, server, onClose, onConfirm }) => {
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState([]);
  const [pasteOpen,   setPasteOpen]   = useState(false);
  const [pasteInput,  setPasteInput]  = useState('');
  const [pasteResult, setPasteResult] = useState(null);
  // pasteResult: { matched: string[], notFound: string[] } | null

  useEffect(() => {
    if (!open || !server) return;
    const all     = server.allSites      ?? [];
    const current = server.selectedSites ?? [];
    setSelected(current.length > 0 ? [...current] : [...all]);
    setSearch('');
    setPasteOpen(false);
    setPasteInput('');
    setPasteResult(null);
  }, [open]);

  if (!server) return null;

  const allSites     = server.allSites ?? [];
  const selectedTacs = Array.isArray(server.tacs)
    ? server.tacs.map(t => (typeof t === 'string' ? t : t.tac)).filter(Boolean)
    : (server.tac ? [server.tac] : []);
  const multiTac     = selectedTacs.length > 1;
  const siteToTac    = server.siteToTac ?? {};
  const siteToSide   = server.siteToSide ?? {};

  const filtered = allSites.filter((s) =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (site) => {
    setSelected((prev) =>
      prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site]
    );
  };

  // Paste apply — match pasted names against allSites (case-insensitive)
  const handlePasteApply = () => {
    const pasted  = pasteInput.split(',').map((s) => s.trim()).filter(Boolean);
    const allLow  = Object.fromEntries(allSites.map((s) => [s.toLowerCase(), s]));
    const matched  = [];
    const notFound = [];
    for (const p of pasted) {
      const real = allLow[p.toLowerCase()];
      if (real) matched.push(real);
      else notFound.push(p);
    }
    // Add matched to current selection (union, no duplicates)
    setSelected((prev) => [...new Set([...prev, ...matched])]);
    setPasteResult({ matched, notFound });
  };

  const selectionColor =
    selected.length === allSites.length ? 'success' :
    selected.length === 0               ? 'error'   : 'warning';

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Site List</Box>
          <Typography variant="caption" color="text.secondary" component="div">
            Server: <strong>{server.omcId}</strong>
            {multiTac
              ? <> · TACs: {selectedTacs.map((t, i) => (
                  <span key={t}>{i > 0 ? ', ' : ' '}<strong>{t}</strong></span>
                ))}</>
              : <> · TAC: <strong>{server.tac}</strong></>
            }
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>

        {/* Search + counter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search sites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Chip label={`${selected.length} / ${allSites.length}`} size="small" color={selectionColor} />
        </Box>

        {/* Quick actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Button size="small" variant="text" onClick={() => setSelected([...allSites])}>
            Select All
          </Button>
          <Button size="small" variant="text" color="error" onClick={() => setSelected([])}>
            Deselect All
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant={pasteOpen ? 'contained' : 'outlined'}
            startIcon={<PlaylistAddIcon />}
            onClick={() => { setPasteOpen((v) => !v); setPasteResult(null); setPasteInput(''); }}
          >
            Paste Sites
          </Button>
        </Box>

        {/* Paste panel */}
        <Collapse in={pasteOpen}>
          <Box sx={{
            mb: 1.5, p: 1.5, border: '1px dashed', borderColor: 'primary.light',
            borderRadius: 1, bgcolor: 'primary.50',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Paste site names separated by commas — they'll be added to the current selection.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small" fullWidth multiline maxRows={4}
                placeholder="e.g. SITE001, SITE002, SITE003"
                value={pasteInput}
                onChange={(e) => { setPasteInput(e.target.value); setPasteResult(null); }}
              />
              <Button
                size="small" variant="contained"
                onClick={handlePasteApply}
                disabled={!pasteInput.trim()}
                sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
              >
                Apply
              </Button>
            </Box>

            {/* Paste result feedback */}
            {pasteResult && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {pasteResult.matched.length > 0 && (
                  <Typography variant="caption" sx={{ color: 'success.dark' }}>
                    ✓ {pasteResult.matched.length} site{pasteResult.matched.length !== 1 ? 's' : ''} added to selection.
                  </Typography>
                )}
                {pasteResult.notFound.length > 0 && (
                  <Box sx={{ p: 0.75, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark', display: 'block' }}>
                      ⚠ Not found ({pasteResult.notFound.length}):
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontFamily: 'monospace' }}>
                      {pasteResult.notFound.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Site list */}
        <Paper variant="outlined" sx={{ maxHeight: 340, overflowY: 'auto' }}>
          <List dense disablePadding>
            {filtered.length === 0 && (
              <ListItem>
                <ListItemText secondary="No sites match the search." />
              </ListItem>
            )}
            {filtered.map((site) => {
              const checked = selected.includes(site);
              const tac     = siteToTac[site];
              const side    = siteToSide[site];
              return (
                <ListItemButton
                  key={site}
                  onClick={() => toggle(site)}
                  sx={{ '&:hover': { bgcolor: 'action.selected' } }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={checked} size="small" disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            color:          checked ? 'text.primary' : 'text.disabled',
                            textDecoration: checked ? 'none'         : 'line-through',
                          }}
                        >
                          {site}
                        </Typography>
                        {multiTac && tac && (
                          <Chip
                            label={tac}
                            size="small"
                            sx={{
                              height: 16, fontSize: '0.6rem', fontWeight: 700,
                              bgcolor: 'primary.50', color: 'primary.dark',
                              border: '1px solid', borderColor: 'primary.light',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {side && (
                          <Chip
                            label={side}
                            size="small"
                            sx={{
                              height: 16, fontSize: '0.6rem', fontWeight: 700,
                              bgcolor: sideColor(side).bg, color: sideColor(side).fg,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={() => onConfirm(selected)} variant="contained" disabled={selected.length === 0}>
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};


export default SiteListDialog;